import { atom, useAtomValue, useSetAtom } from 'jotai';
import { createContext, type FC, type PropsWithChildren, use, useCallback, useRef } from 'react';
import { useAnalytics } from '~/hooks/useAnalytics';
import { AnalyticsEvents } from '~/services/analytics';

function readFromStorage(key: string, fallback: string[]): string[] {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((item) => typeof item === 'string')) {
      return parsed;
    }
    return fallback;
  } catch {
    return fallback;
  }
}

function createColumnsAtoms(storageKey?: string) {
  const initial = storageKey ? readFromStorage(storageKey, []) : [];
  const baseAtom = atom<string[]>(initial);

  const persist = (value: string[]) => {
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(value));
    } catch {
      // Ignore storage errors (e.g. quota exceeded)
    }
  };

  const hiddenColumnsAtom = atom(
    (get) => get(baseAtom),
    (_get, set, value: string[]) => {
      set(baseAtom, value);
      persist(value);
    },
  );

  const toggleColumnAtom = atom(null, (get, set, id: string): 'hidden' | 'shown' => {
    const current = get(baseAtom);
    const isHidden = current.includes(id);
    const next = isHidden ? current.filter((columnId) => columnId !== id) : [...current, id];
    set(hiddenColumnsAtom, next);
    return isHidden ? 'shown' : 'hidden';
  });

  return { hiddenColumnsAtom, toggleColumnAtom, storageKey };
}

type ColumnsAtoms = ReturnType<typeof createColumnsAtoms>;

const TableColumnsContext = createContext<ColumnsAtoms | null>(null);

function useColumnsContext() {
  const ctx = use(TableColumnsContext);
  if (!ctx) throw new Error('useTableColumns must be used inside <TableColumnsProvider>');
  return ctx;
}

interface TableColumnsProviderProps {
  storageKey?: string;
}

export const TableColumnsProvider: FC<PropsWithChildren<TableColumnsProviderProps>> = ({ children, storageKey }) => {
  const atomsRef = useRef<ColumnsAtoms | null>(null);
  if (!atomsRef.current) {
    atomsRef.current = createColumnsAtoms(storageKey);
  }
  return <TableColumnsContext.Provider value={atomsRef.current}>{children}</TableColumnsContext.Provider>;
};

const useHiddenColumns = (): string[] => {
  const { hiddenColumnsAtom } = useColumnsContext();
  return useAtomValue(hiddenColumnsAtom);
};

export const useToggleColumn = () => {
  const { toggleColumnAtom } = useColumnsContext();
  const toggle = useSetAtom(toggleColumnAtom);
  const { track } = useAnalytics();

  return useCallback(
    (id: string) => {
      const status = toggle(id);
      track(AnalyticsEvents.COLUMN_VISIBILITY_TOGGLED, { status, column: id });
    },
    [toggle, track],
  );
};

export const useIsColumnVisible = () => {
  const hidden = useHiddenColumns();
  return useCallback((id: string) => !hidden.includes(id), [hidden]);
};
